import { ref, computed, onMounted } from 'vue'
import { COPILOT_API_URL, copilotHeaders } from '@/config/copilot'
import type { CopilotMessage } from '@/types'

interface ContextPayload {
  projectId: string
  projectName: string
  projectDescription: string
  projectStatus: string
  organizationName: string
  contextDocumentContent?: string
  selectedItems: Array<{
    type: string
    title: string
    content: string
  }>
  templatePrompt?: string
  requirementsSummary?: string
  meetingsSummary?: string
}

interface SSEEvent {
  event: string
  data: string
}

interface CopilotProfile {
  name: string
  label?: string
  tools: string[]
}

interface CopilotProfilesResponse {
  default?: string
  profiles?: CopilotProfile[]
}

interface CopilotProfileOption {
  value: string
  label: string
  description: string
}

const ALL_PROFILE_VALUE = 'all'
const PROFILE_STORAGE_KEY = 'copilot.selectedProfile'

// Etiquetas de respaldo (fallback) por si la API no envía `label` para un perfil.
const PROFILE_LABELS: Record<string, string> = {
  gateway: 'Asistente guiado (router)',
  factory: 'Factory (Dev)',
  sales: 'Comercial',
  pm: 'Gestión (PM)',
  knowledge: 'Conocimiento',
}

function normalizeProfile(profile: string | null): string | null {
  if (!profile || profile === ALL_PROFILE_VALUE) return null
  return profile
}

// Indica si el usuario tiene una preferencia explícita guardada en localStorage.
// Distingue "sin preferencia" (null) de "eligió General/todas las tools" (ALL_PROFILE_VALUE).
function hasStoredPreference(): boolean {
  try {
    return localStorage.getItem(PROFILE_STORAGE_KEY) !== null
  } catch {
    return false
  }
}

function readStoredProfile(): string | null {
  try {
    return normalizeProfile(localStorage.getItem(PROFILE_STORAGE_KEY))
  } catch {
    return null
  }
}

function persistProfile(profile: string | null): void {
  try {
    const normalized = normalizeProfile(profile)
    // Persistimos explícitamente la elección del usuario. Si eligió "General
    // (todas las tools)" guardamos ALL_PROFILE_VALUE para respetar esa preferencia
    // y NO caer al default en la siguiente carga.
    localStorage.setItem(PROFILE_STORAGE_KEY, normalized ?? ALL_PROFILE_VALUE)
  } catch {
    // Ignore storage errors.
  }
}

function getProfileLabel(profile: CopilotProfile): string {
  return (
    profile.label ||
    PROFILE_LABELS[profile.name] ||
    profile.name.charAt(0).toUpperCase() + profile.name.slice(1)
  )
}

function buildContextMessage(message: string, context: ContextPayload): string {
  const parts: string[] = []

  parts.push('[CONTEXTO DEL PROYECTO]')
  parts.push(`Proyecto: ${context.projectName} (${context.projectStatus})`)
  parts.push(`Organización: ${context.organizationName}`)
  if (context.projectDescription) {
    parts.push(`Descripción: ${context.projectDescription}`)
  }
  if (context.contextDocumentContent) {
    parts.push(`\nDocumento de contexto:\n${context.contextDocumentContent}`)
  }
  if (context.requirementsSummary) {
    parts.push(`\nResumen de requerimientos: ${context.requirementsSummary}`)
  }
  if (context.meetingsSummary) {
    parts.push(`\nResumen de reuniones: ${context.meetingsSummary}`)
  }

  if (context.selectedItems.length > 0) {
    parts.push('\nArtefactos seleccionados:')
    for (const item of context.selectedItems) {
      const typeLabel =
        item.type === 'document' ? 'Documento' :
        item.type === 'requirement' ? 'Requerimiento' :
        item.type === 'meeting' ? 'Reunión' :
        item.type === 'application' ? 'Aplicación' :
        item.type === 'attachment' ? 'Adjunto' : item.type

      const truncated = item.content.length > 3000
        ? item.content.substring(0, 3000) + '...'
        : item.content
      parts.push(`- [${typeLabel}] ${item.title}: ${truncated}`)
    }
  }

  parts.push(`\n[MENSAJE DEL USUARIO]\n${message}`)

  return parts.join('\n')
}

async function fetchSSE(
  url: string,
  body: Record<string, unknown>,
  onEvent: (event: string, data: string) => void,
  signal?: AbortSignal
): Promise<void> {
  const response = await fetch(url, {
    method: 'POST',
    headers: await copilotHeaders(),
    body: JSON.stringify(body),
    signal,
  })

  if (!response.ok) {
    const text = await response.text()
    throw new Error(`HTTP ${response.status}: ${text}`)
  }

  const reader = response.body!.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  let reading = true
  while (reading) {
    const { done, value } = await reader.read()
    if (done) {
      reading = false
      continue
    }

    buffer += decoder.decode(value, { stream: true })

    const events = parseSSEBuffer(buffer)
    buffer = events.remaining

    for (const evt of events.parsed) {
      onEvent(evt.event, evt.data)
    }
  }

  // Process any remaining buffer
  if (buffer.trim()) {
    const events = parseSSEBuffer(buffer + '\n\n')
    for (const evt of events.parsed) {
      onEvent(evt.event, evt.data)
    }
  }
}

function parseSSEBuffer(buffer: string): { parsed: SSEEvent[]; remaining: string } {
  const parsed: SSEEvent[] = []
  const blocks = buffer.split('\n\n')
  const remaining = blocks.pop() || ''

  for (const block of blocks) {
    if (!block.trim()) continue

    let event = 'message'
    let data = ''

    for (const line of block.split('\n')) {
      if (line.startsWith('event: ')) {
        event = line.slice(7).trim()
      } else if (line.startsWith('data: ')) {
        data = line.slice(6)
      } else if (line.startsWith('data:')) {
        data = line.slice(5)
      }
    }

    if (data) {
      parsed.push({ event, data })
    }
  }

  return { parsed, remaining }
}

export function useCopilotChat() {
  const sessionId = ref<string | null>(null)
  const messages = ref<CopilotMessage[]>([])
  const isLoading = ref(false)
  const isSending = ref(false)
  const error = ref<string | null>(null)
  const profiles = ref<CopilotProfile[]>([])
  const areProfilesLoading = ref(false)
  const defaultProfile = ref<string | null>(null)
  const selectedProfile = ref<string | null>(readStoredProfile())

  let abortController: AbortController | null = null

  const hasMessages = computed(() => messages.value.length > 0)
  const isSessionActive = computed(() => !!sessionId.value)
  const profileOptions = computed<CopilotProfileOption[]>(() => [
    {
      value: ALL_PROFILE_VALUE,
      label: 'General (todas las tools)',
      description: 'Todas las herramientas disponibles',
    },
    ...profiles.value.map((profile) => ({
      value: profile.name,
      label: getProfileLabel(profile),
      description: `${profile.tools.length} tools disponibles`,
    })),
  ])

  const setSelectedProfile = (profile: string | null): void => {
    const normalized = normalizeProfile(profile)
    selectedProfile.value = normalized
    persistProfile(normalized)
  }

  // Pre-selecciona un perfil SIN persistirlo: se usa para aplicar el default de
  // la API cuando el usuario aún no eligió nada, sin convertirlo en preferencia.
  const applyDefaultProfile = (profile: string | null): void => {
    selectedProfile.value = normalizeProfile(profile)
  }

  const fetchProfiles = async (): Promise<void> => {
    areProfilesLoading.value = true

    try {
      const response = await fetch(`${COPILOT_API_URL}/api/profiles`, {
        headers: await copilotHeaders(),
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data = (await response.json()) as CopilotProfilesResponse
      const validProfiles = Array.isArray(data.profiles)
        ? data.profiles.filter((profile): profile is CopilotProfile =>
          typeof profile?.name === 'string' && Array.isArray(profile.tools)
        )
        : []

      profiles.value = validProfiles
      defaultProfile.value =
        typeof data.default === 'string' ? normalizeProfile(data.default) : null

      const isValidProfile = (name: string | null): boolean =>
        !!name && validProfiles.some((profile) => profile.name === name)

      if (hasStoredPreference()) {
        // El usuario tiene una preferencia guardada. Si es un perfil concreto y
        // sigue existiendo en la lista, la respetamos. Si eligió "General"
        // (sin perfil) también la respetamos. Solo caemos al default si la
        // preferencia era un perfil que ya NO está disponible (ej. el tenant
        // perdió ese módulo).
        if (selectedProfile.value && !isValidProfile(selectedProfile.value)) {
          setSelectedProfile(
            isValidProfile(defaultProfile.value) ? defaultProfile.value : null
          )
        }
      } else {
        // Sin preferencia guardada: PRE-SELECCIONAMOS (sin persistir) el default
        // que envía la API (gateway) si es un perfil válido; si no, "General".
        applyDefaultProfile(
          isValidProfile(defaultProfile.value) ? defaultProfile.value : null
        )
      }
    } catch {
      profiles.value = []
      // Ante error de red no pisamos una preferencia guardada válida ni forzamos
      // un default que no pudimos resolver.
      if (selectedProfile.value && !hasStoredPreference()) {
        applyDefaultProfile(null)
      }
    } finally {
      areProfilesLoading.value = false
    }
  }

  onMounted(() => {
    void fetchProfiles()
  })

  const generateSessionId = (): string => {
    const timestamp = Date.now().toString(36)
    const random = Math.random().toString(36).substring(2, 9)
    return `copilot_${timestamp}_${random}`
  }

  const initSession = (): string => {
    if (abortController) {
      abortController.abort()
      abortController = null
    }

    const newSessionId = generateSessionId()
    sessionId.value = newSessionId
    messages.value = []
    error.value = null

    return newSessionId
  }

  const sendMessage = async (
    message: string,
    context: ContextPayload
  ): Promise<CopilotMessage | null> => {
    if (!sessionId.value) {
      error.value = 'No hay sesión activa'
      return null
    }

    if (!message.trim()) {
      error.value = 'El mensaje no puede estar vacío'
      return null
    }

    isSending.value = true
    error.value = null

    const userMessage: CopilotMessage = {
      id: `msg_${Date.now()}_user`,
      role: 'user',
      content: message.trim(),
      createdAt: new Date(),
    }
    messages.value = [...messages.value, userMessage]

    // Build message with embedded context
    const fullMessage = buildContextMessage(message.trim(), context)

    // Create placeholder for assistant response
    const assistantMessageId = `msg_${Date.now()}_assistant`
    const assistantMessage: CopilotMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      createdAt: new Date(),
    }
    messages.value = [...messages.value, assistantMessage]

    abortController = new AbortController()

    try {
      const requestBody: Record<string, unknown> = {
        sessionId: sessionId.value,
        message: fullMessage,
      }

      if (selectedProfile.value) {
        requestBody.profile = selectedProfile.value
      }

      await fetchSSE(
        `${COPILOT_API_URL}/api/chat/stream`,
        requestBody,
        (event, data) => {
          if (event === 'message') {
            try {
              const parsed = JSON.parse(data)
              if (parsed.content) {
                // Update the assistant message content by appending the chunk
                const idx = messages.value.findIndex((m) => m.id === assistantMessageId)
                if (idx >= 0) {
                  const updated = { ...messages.value[idx] }
                  updated.content += parsed.content
                  const newMessages = [...messages.value]
                  newMessages[idx] = updated
                  messages.value = newMessages
                }
              }
            } catch {
              // If data is plain text, append it directly
              const idx = messages.value.findIndex((m) => m.id === assistantMessageId)
              if (idx >= 0) {
                const updated = { ...messages.value[idx] }
                updated.content += data
                const newMessages = [...messages.value]
                newMessages[idx] = updated
                messages.value = newMessages
              }
            }
          }
        },
        abortController.signal
      )

      const finalMessage = messages.value.find((m) => m.id === assistantMessageId)
      return finalMessage || null
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return null
      }
      const errorMessage =
        err instanceof Error ? err.message : 'Error al enviar el mensaje'
      error.value = errorMessage
      console.error('Copilot send message error:', err)
      // Remove both the user and empty assistant messages on error
      messages.value = messages.value.filter(
        (m) => m.id !== userMessage.id && m.id !== assistantMessageId
      )
      return null
    } finally {
      isSending.value = false
      abortController = null
    }
  }

  const clearError = (): void => {
    error.value = null
  }

  const closeSession = async (): Promise<void> => {
    if (abortController) {
      abortController.abort()
      abortController = null
    }

    // Call DELETE to clean up server-side session
    if (sessionId.value) {
      try {
        await fetch(`${COPILOT_API_URL}/api/chat/${sessionId.value}`, {
          method: 'DELETE',
          headers: await copilotHeaders(),
        })
      } catch {
        // Ignore errors on cleanup
      }
    }

    sessionId.value = null
    messages.value = []
    error.value = null
  }

  return {
    sessionId,
    messages,
    isLoading,
    isSending,
    error,
    profiles,
    areProfilesLoading,
    selectedProfile,
    profileOptions,
    hasMessages,
    isSessionActive,
    initSession,
    sendMessage,
    setSelectedProfile,
    clearError,
    closeSession,
  }
}
